import { useMemo } from "react";
import useCustomInputs from "@/features/customField/api/useCustomInputs";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";

/**
 * KEY FIELDS — dropdown custom fields an admin marked "Key field" (Settings →
 * Custom Fields), e.g. an academy's "Team". A key field gets every chart
 * Country gets: the Dashboard card, a Reports tab (Top / Pipeline / Spread),
 * the Segregation pivot, the "How each person is doing" tab, the Leads-page
 * picker and a pinned column.
 *
 * Every Country endpoint takes `keyFieldId=<id>` and then counts by that
 * field with the Country engine unchanged (server `key-field.ts`), so a card
 * renders a key field by adding that one param — see `KeyDimension.params`.
 *
 * The Country field itself is never listed here, even if someone ticks it:
 * it already has its own surfaces, and listing it would draw them twice.
 *
 * Nor is a key field the server marks `keyFieldHidden` — it is for admins,
 * managers and the field's own managers only (server `viewerSeesKeyField`;
 * owner, 2026-10-23: individual users don't see Team anywhere), so every
 * surface built on this hook drops it for everyone else.
 */
export interface KeyField {
  id: number;
  /** Display name, label overlay first: "Team". */
  name: string;
  /** "Teams". */
  plural: string;
  /** Options the viewer may see (the server strips the rest). */
  options: Array<{ id: number; value: string }>;
}

/** "Team" → "Teams", "Category" → "Categories", "Class" → "Classes". */
export function pluralize(word: string): string {
  const w = word.trim();
  if (!w) return w;
  if (/[^aeiou]y$/i.test(w)) return `${w.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/i.test(w)) return `${w}es`;
  return `${w}s`;
}

/**
 * A key field this viewer may not see (the server marks it on `/custom` and
 * `/specifics`). Filter lists and pickers leave it out; forms keep it.
 */
export const isHiddenKeyField = (f: any): boolean => Boolean(f?.keyFieldHidden);

/** Same detection the server uses for the Country field. */
const isCountryField = (f: any): boolean =>
  f?.spesificTypes === "Country" ||
  String(f?.name ?? "").trim().toLowerCase() === "country";

export function useKeyFields(): { fields: KeyField[]; isLoading: boolean } {
  const { connections, isLoading } = useCustomInputs();

  const fields = useMemo<KeyField[]>(() => {
    const rows = (connections as any[] | undefined) ?? [];
    return rows
      .filter(
        (f) =>
          f?.type === "SELECTOR" &&
          Boolean(f?.isKeyField) &&
          !isHiddenKeyField(f) &&
          (f?.options?.length ?? 0) > 0 &&
          !isCountryField(f),
      )
      .sort((a, b) => Number(a.id) - Number(b.id))
      .map((f) => {
        const name = getCfDisplayName(f);
        return {
          id: Number(f.id),
          name,
          plural: pluralize(name),
          options: (f.options as any[]).map((o) => ({
            id: Number(o.id),
            value: String(o.value),
          })),
        };
      });
  }, [connections]);

  return { fields, isLoading };
}

/** Ids (as strings) of the workspace's key fields — each owns a lead filter section. */
export function useKeyFieldIds(): ReadonlySet<string> {
  const { fields } = useKeyFields();
  return useMemo(() => new Set(fields.map((f) => String(f.id))), [fields]);
}

/**
 * What a "by country" card needs to render for one dimension. Country keeps
 * every existing behaviour (no extra params, flags); a key field adds
 * `keyFieldId` to each request and swaps the copy.
 */
export interface KeyDimension {
  /** Stable id for query keys, dashboard cells and tab ids: "country" | "kf-12". */
  key: string;
  kind: "country" | "key";
  /** Custom field id. Country: null (the server resolves its field and echoes
   *  `countryFieldId`); key field: its id. */
  fieldId: number | null;
  /** "Country" | "Team" */
  singular: string;
  /** "Countries" | "Teams" */
  plural: string;
  /** Extra params EVERY country-endpoint call of this card carries. */
  params: Record<string, string>;
}

export const COUNTRY_DIMENSION: KeyDimension = {
  key: "country",
  kind: "country",
  fieldId: null,
  singular: "Country",
  plural: "Countries",
  params: {},
};

export function keyFieldDimension(f: KeyField): KeyDimension {
  return {
    key: `kf-${f.id}`,
    kind: "key",
    fieldId: f.id,
    singular: f.name,
    plural: f.plural,
    params: { keyFieldId: String(f.id) },
  };
}

/** The workspace's key-field dimensions (Country excluded), oldest first. */
export function useKeyDimensions(): {
  dimensions: KeyDimension[];
  isLoading: boolean;
} {
  const { fields, isLoading } = useKeyFields();
  const dimensions = useMemo(() => fields.map(keyFieldDimension), [fields]);
  return { dimensions, isLoading };
}
